import React, { useState, useEffect } from "react";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { Member, PartnerDetailResponse, Sector } from "../model/types";
import { Link, useNavigate } from "react-router-dom";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { imageList } from "@/core/utils/imageList";
import { partnersService } from "../service/api";
import { $userBasicInfo } from "@/features/user/store";
import { useUnit } from "effector-react";
import { useStartConversation } from "@/features/chat/viewmodel/useChatViewModel";

interface MemberCardProps {
  member: Member;
  type: "user" | "partner";
  partnerId?: string; // Partner ID to fetch sectors from
  onMemberFollow: (memberId: string, isFollowing: boolean) => void;
  showAdminTag?: boolean;
}

export const MemberCard: React.FC<MemberCardProps> = ({
  member,
  onMemberFollow,
  type,
  partnerId,
  showAdminTag,
}) => {
  const [partnerData, setPartnerData] = useState<PartnerDetailResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userBasic = useUnit($userBasicInfo);
  const { startConversation, loading: startingConversation } =
    useStartConversation();
  const [showAllSectors, setShowAllSectors] = useState(false);

  useEffect(() => {
    const fetchPartnerData = async () => {
      if (!partnerId) return;

      setLoading(true);
      try {
        const data = await partnersService.getPartnerById(partnerId);
        setPartnerData(data);
      } catch (error) {
        console.error("Failed to fetch partner data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerData();
  }, [partnerId]);

  const handleFollowClick = () => {
    onMemberFollow(member.id, member.is_followed_by_me);
  };
  const handleEditProfile = () => {
    navigate("/settings");
  };

  return (
    <div className="member-card">
      <div className="member-header">
        <div className="member-avatar-container">
          <div className="member-avatar">
            <BgImage img={member.logo_uri} altImage={imageList.noimage} />
          </div>
          {member.is_admin && showAdminTag && (
            <div className="member-role-badge">Admin</div>
          )}
        </div>
        <div className="member-info">
          <div className="member-name">
            <Link
              to={`${type === "user" ? "/users/" : "/partners/"}${member.id}`}
            >
              {member.firstname} {member.lastname}
            </Link>
          </div>
          <p className="member-title">{member.job_title}</p>
        </div>
      </div>

      <div className="member-expertise">
        <div className="user-tags">
          {member.sectors
            .slice(0, showAllSectors ? member.sectors.length : 3)
            .map((sector, index) => (
              <span key={index} className="tag">
                {sector.name}
              </span>
            ))}
          {member.sectors.length > 3 && (
            <span
              className="tag more"
              onClick={() => setShowAllSectors(!showAllSectors)}
              style={{ cursor: "pointer" }}
            >
              {showAllSectors ? "Show less" : `+${member.sectors.length - 3}`}
            </span>
          )}
        </div>
      </div>

      <div className="member-actions">
        {!member.is_me ? (
          <>
            <button
              className={`follow-btn ${
                member.is_followed_by_me ? "following" : ""
              }`}
              onClick={handleFollowClick}
            >
              <SvgIcon
                icon={
                  member.is_followed_by_me
                    ? "icon-user-minus"
                    : "icon-user-plus"
                }
              />
              {member.is_following_me && !member.is_followed_by_me
                ? "Follow back"
                : member.is_followed_by_me
                  ? "Unfollow"
                  : "Follow"}
            </button>
            <button
              className="message-btn"
              onClick={() => startConversation(member.id)}
              disabled={startingConversation}
              title="Send message"
            >
              <SvgIcon icon="icon-message" />
            </button>
          </>
        ) : (
          <button className={`edit-btn `} onClick={handleEditProfile}>
            <SvgIcon icon={"icon-cog"} />
            Edit profile
          </button>
        )}
      </div>
    </div>
  );
};
